import type { CSSProperties } from "react";
import { SpeakerIcon } from "./icons";

interface AudioButtonProps {
  size?: number;
  label?: string;
  /**
   * Short accent tag (UK / US) rendered next to the icon. Implies the pill
   * shape — a bare circle has no room for text.
   */
  badge?: string;
  /** Filled state while a clip is streaming / speaking. */
  busy?: boolean;
  onPlay?: () => void;
}

/**
 * A single pronunciation trigger. Use `Pronounce` instead of reaching for this
 * directly — it decides how many buttons a word gets based on the audio source
 * chosen in Settings.
 */
export function AudioButton({ size = 32, label = "Tap to hear", badge, busy = false, onPlay }: AudioButtonProps) {
  const pill = badge != null;
  const style = {
    "--btn-size": `${size}px`,
    height: size,
    ...(pill ? null : { width: size }),
  } as CSSProperties;

  return (
    <button
      className={`audio-btn ${pill ? "audio-btn--pill" : ""} ${busy ? "is-busy" : ""}`}
      style={style}
      aria-label={pill ? `${label} — ${badge}` : label}
      title={label}
      onClick={onPlay}
      type="button"
    >
      <SpeakerIcon />
      {pill && <span className="audio-btn__badge t-mono">{badge}</span>}
      <span className="audio-btn__tip t-mono">{label}</span>
    </button>
  );
}
